/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

/**
 * CartSummaryTable is a responsive grid-based component that displays cart items in a table-like format.
 * It adapts to mobile views by switching to a stacked layout with labeled sections.
 * 
 * @component
 */

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes, VComponent } from '@adobe-commerce/elsie/lib';
import { useText } from '@adobe-commerce/elsie/i18n';
import '@/cart/components/CartSummaryTable/CartSummaryTable.css';

/**
 * Represents a single row entry in the cart summary table
 * Each field accepts a VNode to allow for flexible content rendering
 */
export interface CartTableEntry {
  /** The item UID */
  uid: string;
  /** Whether the item is updating */
  updating: boolean;
  /** Whether the item has an update error */
  hasError: boolean;
  /** The product details section (typically includes image, name, and options) */
  item: VNode;
  /** The unit price of the product */
  price: VNode;
  /** The quantity selector/display */
  quantity: VNode;
  /** The total price for this line item */
  subtotal: VNode;
  /** Actions bar on the bottom of the row */
  actions: VNode;
  /** Optional undo banner for removed items */
  undoBanner?: VNode;
}

/**
 * Props for the CartSummaryTable component
 */
export interface CartSummaryTableProps extends HTMLAttributes<HTMLDivElement> {
  /** Array of cart entries to display in the table */
  entries: CartTableEntry[];
  /** Optional CSS class name for custom styling */
  className?: string;
}

/**
 * CartSummaryTable component displays cart items in a responsive grid layout
 * - Uses CSS Grid for layout with 4 columns on desktop
 * - Switches to a stacked single-column layout on mobile with labeled sections
 * - Renders each entry's content using VComponent for proper component handling
 */
export const CartSummaryTable: FunctionComponent<CartSummaryTableProps> = ({
  entries,
  className,
  ...props
}) => {
  const dictionary = useText({
    itemLabel: 'Cart.CartSummaryTable.item',
    priceLabel: 'Cart.CartSummaryTable.price',
    qtyLabel: 'Cart.CartSummaryTable.qty',
    subtotalLabel: 'Cart.CartSummaryTable.subtotal',
    mobilePriceLabel: 'Cart.CartSummaryTable.mobilePrice',
    mobileQtyLabel: 'Cart.CartSummaryTable.mobileQty',
    mobileSubtotalLabel: 'Cart.CartSummaryTable.mobileSubtotal'
  });

  return (
    <div {...props} className={classes(['cart-cart-summary-table', className])}>
      {/* Table header - hidden on mobile */}
      <div className="cart-cart-summary-table__header">
        <div className="cart-cart-summary-table__header-item">{dictionary.itemLabel}</div>
        <div className="cart-cart-summary-table__header-price">{dictionary.priceLabel}</div>
        <div className="cart-cart-summary-table__header-qty">{dictionary.qtyLabel}</div>
        <div className="cart-cart-summary-table__header-subtotal">{dictionary.subtotalLabel}</div>
      </div>
      {/* Table body - adapts to grid layout on mobile */}
      <div className="cart-cart-summary-table__body">
        {entries.map((entry) => {
          // If this entry is an undo banner, render it as a full-width banner
          if (entry.undoBanner) {
            return (
              <div key={entry.uid} className="cart-cart-summary-table__undo-banner">
                <VComponent node={entry.undoBanner} />
              </div>
            );
          }
          
          // Otherwise render as a regular table row
          return (
            <div key={entry.uid} className={classes([
                'cart-cart-summary-table__row',
                [ 'cart-cart-summary-table__row--updating', entry.updating ],
                [ 'cart-cart-summary-table__row--error', entry.hasError ],
              ])}
            >
              <div className="cart-cart-summary-table__cell-item">
                <VComponent node={entry.item} />
              </div>
              <div className="cart-cart-summary-table__cell-price">
                <span className="cart-cart-summary-table__mobile-label">{dictionary.mobilePriceLabel}</span>
                <VComponent node={entry.price} />
              </div>
              <div className="cart-cart-summary-table__cell-qty">
                <span className="cart-cart-summary-table__mobile-label">{dictionary.mobileQtyLabel}</span>
                <VComponent node={entry.quantity} />
              </div>
              <div className="cart-cart-summary-table__cell-subtotal">
                <span className="cart-cart-summary-table__mobile-label">{dictionary.mobileSubtotalLabel}</span>
                <VComponent node={entry.subtotal} />
              </div>
              <div className="cart-cart-summary-table__item-footer">
                <VComponent className="cart-cart-summary-table__item-actions" node={entry.actions} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
